import { useEffect, useState } from 'react';
import { match, useLocation } from 'react-router-dom';
import { styled } from 'styled-components';

import * as api from '../../../../api';

import { Col, Container, Row } from 'react-bootstrap';

import { useSelector } from 'react-redux';
import * as store from '../../../../reducers/store';

import EmbeddedFooter from '../Common/EmbeddedFooter';
import Filters from './Filters';
import Footer from '../../../Schedule/Form/Footer';
import Results from './Results';
import SearchBar from '../Common/SearchBar';

import { EmbeddedFluidContainer } from '../Common';
import { isEnumMember } from 'common/dist/utils/types';
import { updateWindowHistory } from '../Common/utils';
import { useProviders } from '../../../../hooks/provider/useProviders';
import { Button, Loader, Select, Typography } from '@vivaahealth/design-system';
import { sortByDistance, sortByName } from '../Common/utils';
import { assertNever } from 'common/dist/utils/types';

import { ReactComponent as FilterIcon } from '../../../../assets/filter.svg';

const PAGE_LIMIT = 10;

interface Props {
  match: match;
  sendHeight: () => void;
  ipLocation?: Location;
}

export type Location = api.Places.Geometry & {
  region_code: string;
  city: string;
  zip: string;
};

enum SortKinds {
  Distance = 'Distance',
  Alphabetical = 'Alphabetical',
}
const SORT_KINDS = [SortKinds.Distance, SortKinds.Alphabetical];

const Search: React.FC<Props> = ({ match, sendHeight, ipLocation }) => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [filterOptions, setFilterOptions] = useState<
    api.PrevisitProvider.FilterOptions | undefined
  >(undefined);
  const [location, setLocation] = useState<Location | undefined>();
  const [currentSort, setCurrentSort] = useState(SortKinds.Alphabetical);
  const {
    providers,
    supportedLanguages,
    supportedLocations,
    supportedPractices,
    supportedProviderTypes,
    supportedSexes,
    totalPages,
    hasLocationSearch,
    isLoading,
    acceptedPayorWithPlanTypes,
  } = useProviders({
    filterOptions: filterOptions ?? {},
    pageLimit: PAGE_LIMIT,
    setCurrentSort,
  });
  const embedded = useSelector((store: store.All) => store.embedded);
  const organizationId = useSelector(
    (store: store.All) => store.inbound.organizationId,
  );

  const { search, pathname } = useLocation();
  useEffect(() => {
    const searchQuery = new URLSearchParams(search);
    const latitude = searchQuery.get('lat');
    const longitude = searchQuery.get('long');
    const regionCode = searchQuery.get('region_code');
    const city = searchQuery.get('city');
    const zip = searchQuery.get('zip');
    const specialty = searchQuery.get('specialty') ?? undefined;
    const spokenLanguages = searchQuery
      .getAll('spokenLanguage')
      .filter((lang): lang is api.Language.LanguageWithOther =>
        isEnumMember(api.Language.LanguageWithOther, lang),
      );
    const sexes = searchQuery
      .getAll('sex')
      .filter((sex): sex is api.User.Sex => isEnumMember(api.User.Sex, sex));
    const offersVirtualVisits =
      searchQuery.get('offersVirtualVisits') === 'true' ? true : undefined;
    const offersOnlineScheduling =
      searchQuery.get('offersOnlineScheduling') === 'true' ? true : undefined;
    const acceptsNewPatients =
      searchQuery.get('acceptsNewPatients') === 'true' ? true : undefined;
    const withinRadiusMiles: number | undefined = searchQuery.get(
      'withinRadiusMiles',
    )
      ? +searchQuery.get('withinRadiusMiles')!!
      : undefined;
    const practiceIds: string[] = searchQuery.getAll('practice');
    const providerTypes: api.PrevisitProvider.ProviderType[] = searchQuery
      .getAll('providerType')
      .filter(
        (providerType): providerType is api.PrevisitProvider.ProviderType =>
          isEnumMember(api.PrevisitProvider.ProviderType, providerType),
      );
    const payorWithPlanTypes: string[] = searchQuery.getAll('insurances');
    const isPrimaryCareProvider =
      searchQuery.get('isPrimaryCareProvider') === 'true' ? true : undefined;
    const agesServed: api.ProviderOrganizationPreference.AgesServed[] =
      searchQuery
        .getAll('agesServed')
        .filter((a): a is api.ProviderOrganizationPreference.AgesServed =>
          isEnumMember(api.ProviderOrganizationPreference.AgesServed, a),
        );
    const locations: string[] = searchQuery.getAll('location');
    let geometry: api.Places.Geometry | undefined = undefined;
    if (latitude && longitude && regionCode && city && zip) {
      geometry = {
        latitude: parseFloat(latitude),
        longitude: parseFloat(longitude),
      };
      setLocation({
        ...geometry,
        region_code: regionCode,
        city,
        zip,
      });
    } else if (ipLocation) {
      const { latitude, longitude, region_code, city, zip } = ipLocation;
      geometry = {
        latitude,
        longitude,
      };
      searchQuery.append('lat', ipLocation.latitude.toString());
      searchQuery.append('long', ipLocation.longitude.toString());
      searchQuery.append('city', ipLocation.city);
      searchQuery.append('region_code', ipLocation.region_code);
      searchQuery.append('zip', ipLocation.zip.toString());
      updateWindowHistory({
        pathname,
        organizationId,
        params: searchQuery,
        embedded,
      });
      setLocation({
        ...geometry,
        region_code,
        city,
        zip,
      });
    }
    setFilterOptions({
      geometry,
      specialty,
      spokenLanguages: spokenLanguages.length > 0 ? spokenLanguages : undefined,
      sexes,
      offersVirtualVisits,
      offersOnlineScheduling,
      acceptsNewPatients,
      withinRadiusMiles,
      practiceIds,
      providerTypes,
      payorWithPlanTypes,
      isPrimaryCareProvider,
      agesServed,
      locations,
    });
  }, [search, ipLocation, pathname, organizationId, embedded]);

  useEffect(() => {
    if (embedded) {
      sendHeight();
    }
  });

  let offset = (currentPage - 1) * PAGE_LIMIT;

  const handlePageChange = (pageNumber: number) => {
    offset = (pageNumber - 1) * PAGE_LIMIT;
    setCurrentPage(pageNumber);
  };

  const handleShowFilters = () => {
    setShowFilters(true);
  };

  const handleCloseFilters = () => {
    setShowFilters(false);
  };

  const handleChangeSort = (sortKind: SortKinds) => {
    if (!providers) {
      return;
    }
    switch (sortKind) {
      case SortKinds.Distance:
        providers.sort(sortByDistance);
        setCurrentSort(SortKinds[sortKind]);
        break;
      case SortKinds.Alphabetical:
        providers.sort(sortByName);
        setCurrentSort(SortKinds[sortKind]);
        break;
      default:
        assertNever('Unknown sort kind', sortKind);
    }
  };

  if (!filterOptions || !providers) {
    return (
      <Col className="d-flex flex-column px-0">
        <Loader className="m-auto" label="Loading search results..." />
      </Col>
    );
  }

  const header = (
    <Row className="px-3 px-sm-0 pt-sm-3 pb-lg-3 flex-column flex-lg-row">
      <Col className="px-0 d-xs-none">
        <Typography variant="headline-small" bold className="px-0 flex-grow-2">
          {providers.length
            ? `${providers.length} Provider${providers.length === 1 ? '' : 's'}`
            : 'No results'}
        </Typography>
      </Col>
      <Col lg="auto" className="d-flex px-0 mb-3 mt-2 my-lg-0">
        <StyledRoundedButton
          className="d-lg-none border border-light rounded-pill me-1"
          size="small"
          variant="plain"
          onClick={handleShowFilters}
        >
          <FilterIcon />
          <Typography variant="title-medium" bold>
            Filters
          </Typography>
        </StyledRoundedButton>
        <StyledSelect
          name="currentSort"
          value={{
            label: `Sort: ${currentSort}`,
            value: currentSort,
          }}
          options={
            hasLocationSearch
              ? SORT_KINDS.map((k) => ({ label: k, value: k }))
              : SORT_KINDS.filter((k) => k !== SortKinds.Distance).map((k) => ({
                  label: k,
                  value: k,
                }))
          }
          onChange={({ value: newSortKind }: any) =>
            handleChangeSort(newSortKind)
          }
          small={true}
        />
      </Col>
    </Row>
  );

  const content = (
    <>
      <Filters
        showFilters={showFilters}
        closeFilters={handleCloseFilters}
        filterOptions={filterOptions}
        setFilterOptions={setFilterOptions}
        setCurrentPage={setCurrentPage}
        supportedLanguages={supportedLanguages}
        supportedLocations={supportedLocations}
        supportedPractices={supportedPractices}
        supportedProviderTypes={supportedProviderTypes}
        supportedSexes={supportedSexes}
        acceptedPayorWithPlanTypes={acceptedPayorWithPlanTypes}
        ipLocation={ipLocation}
      />
      <Results
        pageOffset={offset}
        pageLimit={PAGE_LIMIT}
        isLoading={isLoading}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        setCurrentPage={setCurrentPage}
        providers={providers}
        totalPages={totalPages}
      />
    </>
  );

  return embedded ? (
    <>
      <EmbeddedFluidContainerWithPadding fluid>
        <EmbeddedFluidContainer fluid>
          <SearchBar
            match={match}
            border={true}
            allowModal={true}
            locationSearch={location}
            setCurrentPage={setCurrentPage}
          />
        </EmbeddedFluidContainer>
        <StyledHR className="m-0 d-none d-sm-block" />
        <EmbeddedFluidContainer fluid>
          <Row className="mx-0">{content}</Row>
        </EmbeddedFluidContainer>
      </EmbeddedFluidContainerWithPadding>
      <EmbeddedFooter />
    </>
  ) : (
    <>
      <Container fluid className="p-0">
        <LimitedContainer className="px-0">
          <SearchBar
            match={match}
            border={true}
            allowModal={true}
            locationSearch={location}
            setCurrentPage={setCurrentPage}
          />
        </LimitedContainer>
        <StyledHR className="m-0 d-none d-sm-block" />
        <LimitedContainer>
          {header}
          <StyledRow className="border-top">{content}</StyledRow>
        </LimitedContainer>
      </Container>
      <StyledFooter />
    </>
  );
};

export default Search;

const StyledHR = styled.hr`
  color: var(--color-border-default-light) !important;
  opacity: 1;
`;

const LimitedContainer = styled(Container)`
  @media (min-width: 1200px) {
    max-width: 1032px;
  }
`;

const EmbeddedFluidContainerWithPadding = styled(EmbeddedFluidContainer)`
  padding-top: 12px;
`;

const StyledFooter = styled(Footer)`
  margin-top: auto;
`;

const StyledSelect = styled(Select)`
  width: 200px;
  padding-right: 0px;
  > div > div:not(.select-menu) {
    border-radius: 50rem;
    height: 32px;
  }
  .single-value {
    font-size: 14px;
    font-weight: 600 !important;
  }
  .control {
    min-height: 32px;
  }
  .value-container {
    height: 32px;
    padding-top: 0;
  }
`;

const StyledRoundedButton = styled(Button)`
  border-color: var(--color-shade-60) !important;
`;

const StyledRow = styled(Row)`
  border-color: var(--color-border-default-light) !important;
`;
